import { useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import VideoCard from '@/components/feature/VideoCard';
import SchemaOrg from '@/components/base/SchemaOrg';
import { videos } from '@/mocks/videos';
import { categories } from '@/mocks/navigation';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const video = useMemo(() => {
    return videos.find((v) => v.id === Number(id));
  }, [id]);

  if (!video) {
    return (
      <Layout>
        <div className="pt-24 pb-20 text-center">
          <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-error-warning-line text-4xl text-foreground-400"></i>
          </div>
          <h2 className="text-xl font-bold font-heading text-foreground-800 mb-2">
            视频未找到
          </h2>
          <p className="text-sm text-foreground-500 mb-6">
            该视频可能已下架或链接不存在
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm font-medium transition-colors whitespace-nowrap"
          >
            <i className="ri-home-line"></i>
            返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  const relatedVideos = useMemo(() => {
    return videos
      .filter((v) => v.id !== video.id && v.category === video.category)
      .slice(0, 6);
  }, [video]);

  const otherVideos = useMemo(() => {
    return videos
      .filter((v) => v.id !== video.id && v.category !== video.category)
      .slice(0, 6);
  }, [video]);

  const categoryName = categories.find((c) => c.slug === video.category)?.name || '';

  const breadcrumbs = [
    { name: '首页', url: '/' },
    { name: categoryName, url: `/category/${video.category}` },
    { name: video.title, url: `/article/${video.id}` },
  ];

  return (
    <Layout>
      <SchemaOrg
        type="BreadcrumbList"
        breadcrumbs={breadcrumbs}
      />
      <SchemaOrg
        type="Article"
        articleData={{
          headline: video.title,
          description: video.description,
          datePublished: '2026-07-15T00:00:00+08:00',
          dateModified: '2026-08-01T00:00:00+08:00',
          authorName: '桔子视频',
          imageUrl: video.thumbnail,
          url: `https://www.juzishipin.com/article/${video.id}`,
        }}
      />
      <SchemaOrg type="WebSite" />

      <article className="pt-24 pb-16">
        <div className="w-full px-4 md:px-6 max-w-[1400px] mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to={`/category/${video.category}`} className="hover:text-primary-600 transition-colors whitespace-nowrap">
              {categoryName}
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-900 font-medium line-clamp-1">{video.title}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="relative aspect-video rounded-xl overflow-hidden mb-6">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  title={video.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-full bg-primary-500/90 flex items-center justify-center cursor-pointer hover:bg-primary-500 transition-colors">
                      <i className="ri-play-fill text-2xl text-white"></i>
                    </div>
                    <span className="text-white text-sm bg-black/50 px-3 py-1 rounded-full">
                      {video.duration}
                    </span>
                  </div>
                </div>
              </div>

              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-4">
                {video.title}
              </h1>

              <div className="flex items-center gap-4 flex-wrap mb-6">
                <span className="flex items-center gap-1 text-sm bg-accent-100 text-accent-700 px-3 py-1 rounded-full font-medium">
                  <i className="ri-star-fill text-accent-500"></i>
                  {video.rating} 分
                </span>
                <span className="text-sm text-foreground-600">
                  <i className="ri-eye-line mr-1"></i>
                  {video.views}次播放
                </span>
                <span className="text-sm text-foreground-600">
                  <i className="ri-time-line mr-1"></i>
                  {video.updateInfo}
                </span>
                <span className="text-sm text-foreground-600">
                  <i className="ri-folder-line mr-1"></i>
                  {categoryName}
                </span>
              </div>

              <div className="flex items-center gap-2 flex-wrap mb-8">
                {video.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs bg-secondary-100 text-secondary-700 px-3 py-1 rounded-full font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="p-6 rounded-xl bg-background-100 border border-background-200 mb-10">
                <h3 className="text-base font-semibold text-foreground-900 mb-3">视频简介</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {video.description}
                </p>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <div className="mb-8">
                  <h3 className="text-base font-semibold text-foreground-900 mb-4 flex items-center gap-2">
                    <i className="ri-thumb-up-line text-primary-500"></i>
                    同类推荐
                  </h3>
                  <div className="flex flex-col gap-4">
                    {relatedVideos.map((v) => (
                      <Link
                        key={v.id}
                        to={`/article/${v.id}`}
                        className="flex gap-3 group cursor-pointer"
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      >
                        <div className="w-32 h-20 rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={v.thumbnail}
                            alt={v.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug mb-1">
                            {v.title}
                          </h4>
                          <div className="flex items-center gap-2 text-xs text-foreground-500">
                            <span className="flex items-center gap-0.5">
                              <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                              {v.rating}
                            </span>
                            <span>{v.views}次播放</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-4 flex items-center gap-2">
                    <i className="ri-fire-line text-accent-500"></i>
                    猜你喜欢
                  </h3>
                  <div className="flex flex-col gap-4">
                    {otherVideos.map((v) => (
                      <Link
                        key={v.id}
                        to={`/article/${v.id}`}
                        className="flex gap-3 group cursor-pointer"
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      >
                        <div className="w-32 h-20 rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={v.thumbnail}
                            alt={v.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug mb-1">
                            {v.title}
                          </h4>
                          <div className="flex items-center gap-2 text-xs text-foreground-500">
                            <span className="flex items-center gap-0.5">
                              <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                              {v.rating}
                            </span>
                            <span>{v.views}次播放</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>
    </Layout>
  );
}